import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    productsSearchUrl: String,
    customersSearchUrl: String,
    salesUrl: String,
    stripeKey: String,
    taxRate: Number
  }

  static targets = [
    "searchInput", "searchResults",
    "searchPanel", "cartPanel", "searchTab", "cartTab", "cartCount",
    "customerSection", "customerSearch", "customerInput", "customerResults",
    "customerDisplay", "customerName", "customerCredit",
    "cartItems", "subtotal", "taxAmount", "taxRow",
    "discountRow", "discountAmount", "creditRow", "creditAmount", "total",
    "cashButton", "cardButton",
    "cashModal", "cashDue", "cashTendered", "changeDue", "cashSubmit",
    "cardModal", "cardAmount", "cardElement", "cardError", "cardProcessing", "cardActions", "cardSubmitBtn",
    "completeModal", "completeOrderNumber", "completeChange", "completeChangeAmount", "completeTotal", "completeTotalAmount"
  ]

  connect() {
    this.cart = []
    this.selectedCustomer = null
    this.searchTimeout = null
    this.customerSearchTimeout = null
    this.stripe = null
    this.elements = null
    this.pendingOrderId = null
    this.pendingOrderData = null
    this.csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
  }

  // Mobile panel switching
  showSearchPanel() {
    this.searchPanelTarget.classList.remove("hidden")
    this.cartPanelTarget.classList.add("hidden")
    this.searchTabTarget.classList.add("text-emerald-400", "border-emerald-400")
    this.searchTabTarget.classList.remove("text-slate-400", "border-transparent")
    this.cartTabTarget.classList.remove("text-emerald-400", "border-emerald-400")
    this.cartTabTarget.classList.add("text-slate-400", "border-transparent")
  }

  showCartPanel() {
    this.searchPanelTarget.classList.add("hidden")
    this.cartPanelTarget.classList.remove("hidden")
    this.cartPanelTarget.classList.add("flex")
    this.cartTabTarget.classList.add("text-emerald-400", "border-emerald-400")
    this.cartTabTarget.classList.remove("text-slate-400", "border-transparent")
    this.searchTabTarget.classList.remove("text-emerald-400", "border-emerald-400")
    this.searchTabTarget.classList.add("text-slate-400", "border-transparent")
  }

  // Product search
  searchProducts() {
    clearTimeout(this.searchTimeout)
    this.searchTimeout = setTimeout(() => this._fetchProducts(), 200)
  }

  async _fetchProducts() {
    const query = this.searchInputTarget.value.trim()
    if (!query) {
      this.searchResultsTarget.innerHTML = '<p class="text-slate-500 text-center py-12 text-sm">Search for products to add to the sale</p>'
      return
    }

    try {
      const response = await fetch(`${this.productsSearchUrlValue}?q=${encodeURIComponent(query)}`, {
        headers: { "Accept": "application/json" }
      })
      const products = await response.json()
      this._renderProducts(products)
    } catch (e) {
      console.error("Product search failed:", e)
    }
  }

  _renderProducts(products) {
    if (products.length === 0) {
      this.searchResultsTarget.innerHTML = '<p class="text-slate-500 text-center py-8 text-sm">No products found</p>'
      return
    }

    this.searchResultsTarget.innerHTML = products.map(p => `
      <button type="button"
              data-action="pos-register#addToCart"
              data-product-id="${p.id}"
              data-product-name="${this._escapeHtml(p.name)}"
              data-product-price="${p.price_cents}"
              data-product-quantity="${p.quantity ?? 999}"
              class="w-full flex items-center justify-between p-3 rounded-lg hover:bg-white/5 active:bg-white/10 transition text-left">
        <div class="min-w-0 flex-1">
          <p class="text-sm font-medium text-white truncate">${this._escapeHtml(p.name)}</p>
          <p class="text-xs text-slate-400">
            ${p.condition ? this._escapeHtml(p.condition) : ''}
            ${p.card_set ? ' · ' + this._escapeHtml(p.card_set) : ''}
            ${p.location ? ' · ' + this._escapeHtml(p.location) : ''}
          </p>
        </div>
        <div class="text-right ml-3 shrink-0">
          <p class="text-sm font-bold text-emerald-400">$${(p.price_cents / 100).toFixed(2)}</p>
          <p class="text-xs text-slate-500">${p.quantity ?? '∞'} in stock</p>
        </div>
      </button>
    `).join('')
  }

  // Cart management
  addToCart(event) {
    const btn = event.currentTarget
    const productId = parseInt(btn.dataset.productId)
    const name = btn.dataset.productName
    const priceCents = parseInt(btn.dataset.productPrice)
    const maxQty = parseInt(btn.dataset.productQuantity)

    const existing = this.cart.find(i => i.product_id === productId)
    if (existing) {
      if (existing.quantity >= maxQty) return
      existing.quantity++
    } else {
      this.cart.push({ product_id: productId, name, price_cents: priceCents, quantity: 1, max_quantity: maxQty })
    }

    this._renderCart()
    this.updateTotals()
    this.searchInputTarget.focus()
    this.searchInputTarget.select()
  }

  incrementItem(event) {
    const idx = parseInt(event.currentTarget.dataset.index)
    const item = this.cart[idx]
    if (item && item.quantity < item.max_quantity) {
      item.quantity++
      this._renderCart()
      this.updateTotals()
    }
  }

  decrementItem(event) {
    const idx = parseInt(event.currentTarget.dataset.index)
    const item = this.cart[idx]
    if (item) {
      if (item.quantity > 1) {
        item.quantity--
      } else {
        this.cart.splice(idx, 1)
      }
      this._renderCart()
      this.updateTotals()
    }
  }

  removeFromCart(event) {
    const idx = parseInt(event.currentTarget.dataset.index)
    this.cart.splice(idx, 1)
    this._renderCart()
    this.updateTotals()
  }

  clearCart() {
    this.cart = []
    this.selectedCustomer = null
    this._renderCart()
    this.updateTotals()
    this._showCustomerSearch()
    this.searchInputTarget.focus()
  }

  _renderCart() {
    if (this.cart.length === 0) {
      this.cartItemsTarget.innerHTML = '<p class="text-slate-500 text-center py-8 text-sm">Cart is empty</p>'
      if (this.hasCartCountTarget) this.cartCountTarget.textContent = "0"
      return
    }

    const totalItems = this.cart.reduce((sum, i) => sum + i.quantity, 0)
    if (this.hasCartCountTarget) this.cartCountTarget.textContent = totalItems

    this.cartItemsTarget.innerHTML = this.cart.map((item, idx) => `
      <div class="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
        <div class="min-w-0 flex-1">
          <p class="text-sm text-white font-medium truncate">${this._escapeHtml(item.name)}</p>
          <p class="text-xs text-slate-400">$${(item.price_cents / 100).toFixed(2)} each</p>
        </div>
        <div class="flex items-center gap-2 ml-2 shrink-0">
          <button type="button" data-action="pos-register#decrementItem" data-index="${idx}"
                  class="w-7 h-7 rounded bg-slate-800 hover:bg-slate-700 text-white text-sm flex items-center justify-center">-</button>
          <span class="text-sm text-white w-6 text-center">${item.quantity}</span>
          <button type="button" data-action="pos-register#incrementItem" data-index="${idx}"
                  class="w-7 h-7 rounded bg-slate-800 hover:bg-slate-700 text-white text-sm flex items-center justify-center">+</button>
          <span class="text-sm text-white font-medium w-16 text-right">$${(item.price_cents * item.quantity / 100).toFixed(2)}</span>
          <button type="button" data-action="pos-register#removeFromCart" data-index="${idx}"
                  class="text-slate-500 hover:text-red-400 ml-1">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    `).join('')
  }

  // Totals
  updateTotals() {
    const subtotalCents = this.cart.reduce((sum, i) => sum + (i.price_cents * i.quantity), 0)
    const taxCents = this.taxRateValue > 0 ? Math.round(subtotalCents * this.taxRateValue / 100) : 0
    const totalCents = subtotalCents + taxCents

    this.subtotalTarget.textContent = `$${(subtotalCents / 100).toFixed(2)}`

    if (taxCents > 0) {
      this.taxRowTarget.classList.remove("hidden")
      this.taxAmountTarget.textContent = `$${(taxCents / 100).toFixed(2)}`
    } else {
      this.taxRowTarget.classList.add("hidden")
    }

    this.totalTarget.textContent = `$${(totalCents / 100).toFixed(2)}`

    const hasItems = this.cart.length > 0
    this.cashButtonTarget.disabled = !hasItems
    this.cardButtonTarget.disabled = !hasItems
  }

  // Customer search
  searchCustomers() {
    clearTimeout(this.customerSearchTimeout)
    this.customerSearchTimeout = setTimeout(() => this._fetchCustomers(), 300)
  }

  async _fetchCustomers() {
    const query = this.customerInputTarget.value.trim()
    if (!query) {
      this.customerResultsTarget.innerHTML = ''
      return
    }

    try {
      const response = await fetch(`${this.customersSearchUrlValue}?q=${encodeURIComponent(query)}`, {
        headers: { "Accept": "application/json" }
      })
      const customers = await response.json()
      this._renderCustomerResults(customers)
    } catch (e) {
      console.error("Customer search failed:", e)
    }
  }

  _renderCustomerResults(customers) {
    if (customers.length === 0) {
      this.customerResultsTarget.innerHTML = '<p class="text-slate-500 text-xs py-2">No customers found</p>'
      return
    }

    this.customerResultsTarget.innerHTML = customers.map(c => `
      <button type="button"
              data-action="pos-register#selectCustomer"
              data-customer-id="${c.id}"
              data-customer-name="${this._escapeHtml(c.name)}"
              data-customer-email="${this._escapeHtml(c.email)}"
              data-customer-credit="${c.credit_cents}"
              class="w-full flex items-center justify-between p-2 rounded hover:bg-white/5 text-left text-sm">
        <div>
          <span class="text-white">${this._escapeHtml(c.name)}</span>
          <span class="text-slate-500 text-xs ml-2">${this._escapeHtml(c.email)}</span>
        </div>
        ${c.credit_cents > 0 ? `<span class="text-emerald-400 text-xs">$${(c.credit_cents / 100).toFixed(2)} credit</span>` : ''}
      </button>
    `).join('')
  }

  selectCustomer(event) {
    const btn = event.currentTarget
    this.selectedCustomer = {
      id: parseInt(btn.dataset.customerId),
      name: btn.dataset.customerName,
      email: btn.dataset.customerEmail,
      creditCents: parseInt(btn.dataset.customerCredit)
    }

    this.customerNameTarget.textContent = this.selectedCustomer.name
    this.customerCreditTarget.textContent = this.selectedCustomer.creditCents > 0
      ? `$${(this.selectedCustomer.creditCents / 100).toFixed(2)} credit`
      : ''

    this.customerSearchTarget.classList.add("hidden")
    this.customerDisplayTarget.classList.remove("hidden")
    this.customerResultsTarget.innerHTML = ''
  }

  clearCustomer() {
    this.selectedCustomer = null
    this._showCustomerSearch()
  }

  _showCustomerSearch() {
    this.customerSearchTarget.classList.remove("hidden")
    this.customerDisplayTarget.classList.add("hidden")
    this.customerInputTarget.value = ''
    this.customerResultsTarget.innerHTML = ''
  }

  // Cash payment
  payCash() {
    if (this.cart.length === 0) return

    const totalCents = this._calculateTotalCents()
    this.cashDueTarget.textContent = `$${(totalCents / 100).toFixed(2)}`
    this.cashTenderedTarget.value = ''
    this.changeDueTarget.textContent = '$0.00'
    this.cashSubmitTarget.disabled = true
    this.cashModalTarget.classList.remove("hidden")
    this.cashTenderedTarget.focus()
  }

  quickCash(event) {
    const amount = parseInt(event.currentTarget.dataset.amount)
    this.cashTenderedTarget.value = (amount / 100).toFixed(2)
    this.calculateChange()
  }

  quickCashExact() {
    const totalCents = this._calculateTotalCents()
    this.cashTenderedTarget.value = (totalCents / 100).toFixed(2)
    this.calculateChange()
  }

  calculateChange() {
    const totalCents = this._calculateTotalCents()
    const tenderedCents = Math.round(parseFloat(this.cashTenderedTarget.value || 0) * 100)
    const changeCents = Math.max(tenderedCents - totalCents, 0)

    this.changeDueTarget.textContent = `$${(changeCents / 100).toFixed(2)}`
    this.cashSubmitTarget.disabled = tenderedCents < totalCents
  }

  closeCashModal() {
    this.cashModalTarget.classList.add("hidden")
  }

  async submitCashSale() {
    const tenderedCents = Math.round(parseFloat(this.cashTenderedTarget.value || 0) * 100)
    this.cashSubmitTarget.disabled = true
    this.cashSubmitTarget.textContent = "Processing..."

    try {
      const result = await this._submitSale("cash", { cash_tendered_cents: tenderedCents })
      if (result.success) {
        this.closeCashModal()
        this._showComplete(result.order, result.payment)
      } else {
        alert(result.error || "Sale failed")
      }
    } catch (e) {
      alert("Sale failed: " + e.message)
    } finally {
      this.cashSubmitTarget.disabled = false
      this.cashSubmitTarget.textContent = "Complete Sale"
    }
  }

  // Card payment
  async payCard() {
    if (this.cart.length === 0) return
    if (!this.stripeKeyValue) {
      alert("Card payments are not set up. Please configure Stripe in your dashboard.")
      return
    }

    const totalCents = this._calculateTotalCents()
    this.cardAmountTarget.textContent = `$${(totalCents / 100).toFixed(2)}`
    this.cardErrorTarget.classList.add("hidden")
    this.cardProcessingTarget.classList.add("hidden")
    this.cardActionsTarget.classList.remove("hidden")
    this.cardModalTarget.classList.remove("hidden")

    // Create the sale first to get client_secret
    this.cardSubmitBtnTarget.disabled = true
    this.cardSubmitBtnTarget.textContent = "Setting up..."

    try {
      const result = await this._submitSale("card")
      if (!result.success) {
        this.cardErrorTarget.textContent = result.error || "Failed to create sale"
        this.cardErrorTarget.classList.remove("hidden")
        this.cardSubmitBtnTarget.disabled = false
        this.cardSubmitBtnTarget.textContent = "Pay"
        return
      }

      this.pendingOrderId = result.order.id
      this.pendingOrderData = result.order

      if (result.payment.complete) {
        // Order total was 0 (all covered by credit)
        this.closeCardModal()
        this._showComplete(result.order, result.payment)
        return
      }

      // Mount Stripe Payment Element
      if (!this.stripe) {
        this.stripe = Stripe(this.stripeKeyValue)
      }

      this.elements = this.stripe.elements({
        clientSecret: result.payment.client_secret,
        appearance: {
          theme: 'night',
          variables: { colorPrimary: '#10b981' }
        }
      })

      const paymentElement = this.elements.create('payment')
      this.cardElementTarget.innerHTML = ''
      paymentElement.mount(this.cardElementTarget)

      this.cardSubmitBtnTarget.disabled = false
      this.cardSubmitBtnTarget.textContent = "Pay"
    } catch (e) {
      this.cardErrorTarget.textContent = "Failed to set up payment: " + e.message
      this.cardErrorTarget.classList.remove("hidden")
      this.cardSubmitBtnTarget.disabled = false
      this.cardSubmitBtnTarget.textContent = "Pay"
    }
  }

  async submitCardPayment() {
    this.cardSubmitBtnTarget.disabled = true
    this.cardActionsTarget.classList.add("hidden")
    this.cardProcessingTarget.classList.remove("hidden")
    this.cardErrorTarget.classList.add("hidden")

    try {
      const { error } = await this.stripe.confirmPayment({
        elements: this.elements,
        redirect: 'if_required'
      })

      if (error) {
        this.cardErrorTarget.textContent = error.message
        this.cardErrorTarget.classList.remove("hidden")
        this.cardProcessingTarget.classList.add("hidden")
        this.cardActionsTarget.classList.remove("hidden")
        this.cardSubmitBtnTarget.disabled = false
        return
      }

      // Payment succeeded, call complete_payment
      const completeUrl = this.salesUrlValue.replace(/\/sales$/, `/sales/${this.pendingOrderId}/complete_payment`)
      const response = await fetch(completeUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken
        }
      })
      const result = await response.json()

      if (result.success) {
        this.closeCardModal()
        this._showComplete(this.pendingOrderData, { type: "card", complete: true })
      } else {
        this.cardErrorTarget.textContent = result.error || "Failed to confirm payment"
        this.cardErrorTarget.classList.remove("hidden")
      }
    } catch (e) {
      this.cardErrorTarget.textContent = "Payment failed: " + e.message
      this.cardErrorTarget.classList.remove("hidden")
    } finally {
      this.cardProcessingTarget.classList.add("hidden")
      this.cardActionsTarget.classList.remove("hidden")
      this.cardSubmitBtnTarget.disabled = false
    }
  }

  closeCardModal() {
    this.cardModalTarget.classList.add("hidden")
    this.cardElementTarget.innerHTML = ''
    this.elements = null
    this.pendingOrderId = null
    this.pendingOrderData = null
  }

  // Submit sale to server
  async _submitSale(paymentMethod, extra = {}) {
    const body = {
      items: this.cart.map(i => ({
        product_id: i.product_id,
        quantity: i.quantity,
        price_cents: i.price_cents
      })),
      payment_method: paymentMethod,
      customer_name: this.selectedCustomer?.name || "Walk-in Customer",
      ...extra
    }

    if (this.selectedCustomer) {
      body.customer_id = this.selectedCustomer.id
      body.customer_email = this.selectedCustomer.email
    }

    const response = await fetch(this.salesUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken,
        "Accept": "application/json"
      },
      body: JSON.stringify(body)
    })

    return response.json()
  }

  // Complete modal
  _showComplete(order, payment) {
    this.completeOrderNumberTarget.textContent = `Order #${order.order_number}`
    this.completeTotalAmountTarget.textContent = `$${(order.total_cents / 100).toFixed(2)}`

    if (payment.type === "cash" && payment.change_cents > 0) {
      this.completeChangeTarget.classList.remove("hidden")
      this.completeChangeAmountTarget.textContent = `$${(payment.change_cents / 100).toFixed(2)}`
    } else {
      this.completeChangeTarget.classList.add("hidden")
    }

    this.completeModalTarget.classList.remove("hidden")
  }

  newSale() {
    this.completeModalTarget.classList.add("hidden")
    this.cart = []
    this.selectedCustomer = null
    this._renderCart()
    this.updateTotals()
    this._showCustomerSearch()
    this.searchInputTarget.value = ''
    this.searchResultsTarget.innerHTML = '<p class="text-slate-500 text-center py-12 text-sm">Search for products to add to the sale</p>'
    this.searchInputTarget.focus()
  }

  // Helpers
  _calculateTotalCents() {
    const subtotalCents = this.cart.reduce((sum, i) => sum + (i.price_cents * i.quantity), 0)
    const taxCents = this.taxRateValue > 0 ? Math.round(subtotalCents * this.taxRateValue / 100) : 0
    return subtotalCents + taxCents
  }

  _escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML.replace(/"/g, '&quot;').replace(/'/g, '&#39;')
  }
}
